import React, { useState, useRef } from 'react';
import './VoiceInputDebug.css';

// Get configuration from environment variables
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID || '';
const BOT_ID = process.env.REACT_APP_LEX_BOT_ID || '';
const BOT_ALIAS_ID = process.env.REACT_APP_LEX_BOT_ALIAS_ID || '';

interface VoiceInputDebugProps {
  onResult: (text: string) => void;
}

/**
 * A simplified version of VoiceInput for debugging purposes
 * This component focuses on capturing audio and displaying detailed information
 * without the complexity of Lex integration
 */
const VoiceInputDebug: React.FC<VoiceInputDebugProps> = ({ onResult }) => {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioInfo, setAudioInfo] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  
  // Start recording
  const startListening = async () => {
    try {
      setError(null);
      setAudioInfo(null);
      setAudioUrl(null);
      
      console.log('Starting audio recording for debugging...');
      
      // Check supported MIME types
      const supportedMimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/wav',
        'audio/mp4'
      ].filter(mimeType => MediaRecorder.isTypeSupported(mimeType));
      
      console.log('Supported MIME types:', supportedMimeTypes);
      
      if (supportedMimeTypes.length === 0) {
        throw new Error('No supported audio MIME types found');
      }
      
      // Use the first supported MIME type
      const mimeType = supportedMimeTypes[0];
      console.log('Using MIME type:', mimeType);
      
      // Get audio stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create media recorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 16000
      });
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Combine audio chunks into a single blob
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log('Recorded audio blob:', audioBlob.size, 'bytes, type:', audioBlob.type);
        
        // Create a URL for the audio blob
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        
        try {
          // Get audio information
          const audioInfo = await getAudioInfo(audioBlob);
          setAudioInfo(audioInfo);
          
          // Simulate a transcription result
          onResult('This is a debug recording. Check the console for details.');
        } catch (err) {
          console.error('Error processing audio:', err);
          if (err instanceof Error) {
            setError(`Error: ${err.message}`);
          } else {
            setError('Unknown error processing audio');
          }
        }
        
        // Stop all tracks in the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsListening(true);
    } catch (err) {
      console.error('Error starting voice recording:', err);
      if (err instanceof Error) {
        setError(`Failed to start recording: ${err.message}`);
      } else {
        setError('Failed to start recording');
      }
    }
  };
  
  // Stop recording
  const stopListening = () => {
    if (mediaRecorderRef.current && isListening) {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };
  
  // Get audio information
  const getAudioInfo = async (audioBlob: Blob): Promise<string> => {
    try {
      // Create an audio context
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      
      // Convert blob to array buffer
      const arrayBuffer = await audioBlob.arrayBuffer();
      
      // Decode the audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Build information string
      const info = [
        `Duration: ${audioBuffer.duration.toFixed(2)} seconds`,
        `Sample Rate: ${audioBuffer.sampleRate} Hz`,
        `Channels: ${audioBuffer.numberOfChannels}`,
        `Size: ${arrayBuffer.byteLength} bytes`,
        `Format: ${audioBlob.type}`,
        `AWS Config:`,
        `- Identity Pool: ${IDENTITY_POOL_ID}`,
        `- Bot ID: ${BOT_ID}`,
        `- Bot Alias ID: ${BOT_ALIAS_ID}`
      ].join('\n');
      
      return info;
    } catch (error) {
      console.error('Error getting audio info:', error);
      throw new Error('Failed to analyze audio');
    }
  };
  
  return (
    <div className="voice-input-debug">
      <button
        className={`voice-button ${isListening ? 'listening' : ''}`}
        onClick={isListening ? stopListening : startListening}
        aria-label={isListening ? 'Stop recording' : 'Start recording'}
        title={isListening ? 'Stop recording' : 'Start recording'}
      >
        <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
        <span className="debug-label">{isListening ? 'Stop' : 'Debug Record'}</span>
      </button>
      
      {error && <div className="voice-error">{error}</div>}
      
      {audioInfo && (
        <div className="audio-info">
          <h4>Audio Information:</h4>
          <pre>{audioInfo}</pre>
        </div>
      )}
      
      {audioUrl && (
        <div className="audio-player">
          <h4>Recorded Audio:</h4>
          <audio controls src={audioUrl}></audio>
        </div>
      )}
    </div>
  );
};

export default VoiceInputDebug;